import * as yup from 'yup'
import { countryFormats } from '@/helpers/countryFormats'

export const useValidationSchemas = () => {
  const { $t } = useNuxtApp().vueApp.config.globalProperties

  const regexSocials = /^[a-zA-Z0-9_]{5,}$/u
  const userFormRegex = /^[A-Za-zА-Яа-яЁё]+$/u

  const phoneSchema = yup
    .string()
    .transform((val) => val?.trim())
    .required($t('pages.customers_create.phone_required'))
    .test(
      'phone-format',
      $t('pages.customers_create.phone_required'),
      function (val) {
        if (!val) return false

        const inputDigits = val.replace(/\D/g, '')

        const foundCountry = Object.entries(countryFormats).find(
          ([, format]) => {
            const code = format.code.replaceAll('+', '')
            return inputDigits.startsWith(code)
          }
        )

        if (!foundCountry) {
          return this.createError({
            message: $t('pages.customers_create.unknown_code_country'),
          })
        }

        const [countryCode, format] = foundCountry
        const countryCodeDigits = format.code.replace(/\D/g, '')

        if (!inputDigits.startsWith(countryCodeDigits)) {
          return this.createError({
            message: $t('pages.customers_create.invalikd_country_code'),
          })
        }

        const numberDigits = inputDigits.slice(countryCodeDigits.length)
        const expectedDigits = (format.mask.match(/#/g) || []).length

        return (
          numberDigits.length === expectedDigits ||
          this.createError({
            message: $t('pages.customers_create.number_length'),
            params: { country: countryCode, count: expectedDigits },
          })
        )
      }
    )

  const emailSchema = yup
    .string()
    .email($t('forms.errors.invalidEmail'))
    .required($t('forms.errors.fieldRequired'))

  const defaultSchema = yup.string().required($t('forms.errors.fieldRequired'))

  const dateSchema = yup.date().required($t('forms.errors.fieldRequired'))

  const vkSchema = yup
    .string()
    .min(5, $t('forms.errors.vkTooShort'))
    .matches(regexSocials, $t('forms.errors.invalidVk'))
    .required($t('forms.errors.fieldRequired'))

  const telegramSchema = yup
    .string()
    .min(5, $t('forms.errors.telegramTooShort'))
    .matches(regexSocials, $t('forms.errors.invalidTelegram'))
    .required($t('forms.errors.fieldRequired'))

  const nameSchema = yup
    .string()
    .min(3, $t('forms.errors.name-required'))
    .matches(userFormRegex, $t('forms.errors.only_letters'))
    .required($t('forms.errors.fieldRequired'))

  const surnameSchema = yup
    .string()
    .min(3, $t('forms.errors.surname-required'))
    .matches(userFormRegex, $t('forms.errors.only_letters'))
    .required($t('forms.errors.fieldRequired'))

  const middlenameSchema = yup
    .string()
    .min(3, $t('forms.errors.middlename-required'))
    .matches(userFormRegex, $t('forms.errors.only_letters'))
    .required($t('forms.errors.fieldRequired'))

  const userSchema = yup
    .string()
    .min(3, $t('pages.customers_create.not_user_selected'))
    .matches(userFormRegex, $t('forms.errors.only_letters'))
    .required($t('forms.errors.fieldRequired'))

  const fioSchema = yup
    .string()
    .min(3, $t('forms.errors.fio-required'))
    .matches(userFormRegex, $t('forms.errors.only_letters'))
    .required($t('forms.errors.fieldRequired'))

  const passwordSchema = yup
    .string()
    .min(6, $t('forms.errors.password-required'))
    .required($t('forms.errors.fieldRequired'))

  const currentPasswordSchema = yup
    .string()
    .min(6, $t('forms.errors.current-password-required'))
    .required($t('forms.errors.fieldRequired'))

  const newPasswordSchema = yup
    .string()
    .min(6, $t('forms.errors.new-password-required'))
    .required($t('forms.errors.fieldRequired'))

  const passwordFieldsSchema = yup
    .object()
    .shape({
      newPassword: yup
        .string()
        .min(6, $t('forms.errors.new-password-required'))
        .required($t('forms.errors.fieldRequired')),
      confirmNewPassword: yup
        .string()
        .required($t('forms.errors.fieldRequired')),
    })
    .test(
      'passwords-match',
      $t('forms.errors.passwords-not-match'),
      function (value) {
        return (
          value.newPassword === value.confirmNewPassword ||
          this.createError({
            path: 'confirmNewPassword',
            message: $t('forms.errors.passwords-not-match'),
          })
        )
      }
    )

  return {
    phoneSchema,
    emailSchema,
    defaultSchema,
    vkSchema,
    telegramSchema,
    nameSchema,
    surnameSchema,
    middlenameSchema,
    userSchema,
    fioSchema,
    passwordSchema,
    currentPasswordSchema,
    newPasswordSchema,
    passwordFieldsSchema,
    dateSchema,
  }
}
